import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import OandaBroker from "@/components/OandaBroker";
import { buildNextEvt, eventWithin } from "@/lib/fxEvents";
import { AmountChips } from "@/components/widgets";
import { CurrencyDollar, ArrowClockwise, XCircle, CaretUp, CaretDown, Info, BellSimple, CalendarBlank, Calculator, Bank, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (n, d = 2) => (n === null || n === undefined ? "—" : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));
const pairLabel = (p) => `${p.slice(0, 3)}/${p.slice(3)}`;

const fmtCountdown = (iso) => {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return "now";
  const s = Math.floor(diff / 1000);
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${String(sec).padStart(2, "0")}s`;
};

function EventTag({ pair, nextEvt, today }) {
  const isToday = today.has(pair.base) || today.has(pair.quote);
  let soon = null, soonCcy = null;
  [[nextEvt[pair.base], pair.base], [nextEvt[pair.quote], pair.quote]].forEach(([e, c]) => {
    if (e && (!soon || new Date(e.date).getTime() < new Date(soon.date).getTime())) { soon = e; soonCcy = c; }
  });
  if (!soon && !isToday) return null;
  const cls = isToday ? "bg-signal-red/15 text-signal-red border-signal-red/30" : "bg-signal-yellow/15 text-signal-yellow border-signal-yellow/30";
  return (
    <span data-testid={`fx-event-${pair.pair}`} title={soon ? `${soonCcy}: ${soon.title} in ${fmtCountdown(soon.date)}` : "High-impact event today"}
      className={`inline-flex items-center gap-0.5 mono text-[8px] uppercase px-1 py-0.5 rounded-sm border ${cls}`}>
      <Warning size={9} weight="fill" /> {soon ? `${soonCcy} ${fmtCountdown(soon.date)}` : "event"}
    </span>
  );
}

const ALERT_TYPES = [
  ["price_above", "Price rises above"],
  ["price_below", "Price falls below"],
  ["score_above", "AI Score rises above"],
  ["score_below", "AI Score falls below"],
];

function Stat({ label, value, accent }) {
  return (
    <div className="flex-1 min-w-[120px]">
      <div className="label-xs text-muted-foreground">{label}</div>
      <div className={`mono text-lg font-semibold ${accent || ""}`}>{value}</div>
    </div>
  );
}

export default function Forex() {
  const [pairs, setPairs] = useState(null);
  const [acct, setAcct] = useState(null);
  const [ticket, setTicket] = useState({ pair: "EURUSD", side: "buy", units: "10000" });
  const [amtMode, setAmtMode] = useState("units"); // "units" | "usd"
  const [usdAmt, setUsdAmt] = useState("1000");
  const [routeLive, setRouteLive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [oanda, setOanda] = useState(null);            // {linked, active_env}
  const [eventCcy, setEventCcy] = useState(new Set()); // currencies with a high-impact event today
  const [nextEvt, setNextEvt] = useState({});          // currency -> {date, title} soonest upcoming
  const [, setTick] = useState(0);                     // 1s ticker for live countdowns
  useEffect(() => { const id = setInterval(() => setTick((t) => t + 1), 1000); return () => clearInterval(id); }, []);

  const loadPairs = useCallback(() => { api.get("/forex").then((r) => setPairs(r.data.pairs)).catch(() => setPairs([])); }, []);
  const loadAcct = useCallback(() => { api.get("/forex/account").then((r) => setAcct(r.data)).catch(() => {}); }, []);
  const loadOanda = useCallback(() => { api.get("/oanda/status").then((r) => setOanda(r.data)).catch(() => setOanda(null)); }, []);
  const loadEvents = useCallback(() => {
    api.get("/forex/economic?impact=high").then((r) => {
      const today = new Date().toDateString();
      const todaySet = new Set();
      (r.data.events || []).forEach((e) => {
        if (!e.currency || e.currency === "All") return;
        try { if (new Date(e.date).toDateString() === today) todaySet.add(e.currency); } catch {}
      });
      setEventCcy(todaySet);
      setNextEvt(buildNextEvt(r.data.events));
    }).catch(() => {});
  }, []);
  useEffect(() => { loadPairs(); loadAcct(); loadOanda(); loadEvents(); }, [loadPairs, loadAcct, loadOanda, loadEvents]);
  useEffect(() => {
    window.addEventListener("oanda-status-changed", loadOanda);
    return () => window.removeEventListener("oanda-status-changed", loadOanda);
  }, [loadOanda]);

  const pairKey = (pairs || []).map((p) => p.pair).join(",");
  const [liveFeed, setLiveFeed] = useState(false);
  useEffect(() => {
    if (!oanda?.linked || !pairKey) { setLiveFeed(false); return undefined; }
    const names = pairKey.split(",");
    const chunks = [];
    for (let i = 0; i < names.length; i += 20) chunks.push(names.slice(i, i + 20));
    let buf = {};
    const sources = chunks.map((c) => {
      const es = new EventSource(`/api/oanda/stream?pairs=${c.join(",")}`, { withCredentials: true });
      es.addEventListener("hello", () => setLiveFeed(true));
      es.addEventListener("error", () => { if (es.readyState === EventSource.CLOSED) setLiveFeed(false); });
      es.onmessage = (m) => { try { const t = JSON.parse(m.data); buf[t.pair] = t; } catch { /* ignore */ } };
      return es;
    });
    const flush = setInterval(() => {
      const upd = buf; buf = {};
      if (!Object.keys(upd).length) return;
      setPairs((cur) => (cur ? cur.map((p) => (upd[p.pair] ? { ...p, price: upd[p.pair].mid, live: true } : p)) : cur));
    }, 500);
    return () => { clearInterval(flush); sources.forEach((s) => s.close()); setLiveFeed(false); };
  }, [oanda?.linked, pairKey]);

  const priceOf = (p) => (pairs || []).find((x) => x.pair === p)?.price;
  const baseOf = (p) => (pairs || []).find((x) => x.pair === p)?.base;
  // USD value of 1 unit of a currency, derived from the loaded pair prices.
  const usdPerBase = (ccy) => {
    if (!ccy) return null;
    if (ccy === "USD") return 1;
    const d = priceOf(`${ccy}USD`); if (d) return d;      // e.g. EURUSD price = USD per EUR
    const inv = priceOf(`USD${ccy}`); if (inv) return 1 / inv; // e.g. USDJPY → USD per JPY
    return null;
  };
  const ticketUnits = () => {
    if (amtMode === "usd") {
      const upb = usdPerBase(baseOf(ticket.pair));
      if (!upb || !(Number(usdAmt) > 0)) return null;
      return Math.round(Number(usdAmt) / upb);
    }
    return parseFloat(ticket.units);
  };

  const submit = async () => {
    const units = ticketUnits();
    if (!units || units <= 0) return toast.error(amtMode === "usd" ? "Enter a valid $ amount (price may still be loading)" : "Enter a valid unit size");
    const liveBroker = oanda?.linked && oanda?.active_env === "live";
    setBusy(true);
    try {
      if (routeLive && liveBroker) {
        const guard = eventWithin(nextEvt, ticket.pair, 60);
        if (guard && !window.confirm(`⚠ High-impact ${guard.currency} event ("${guard.title}") within the hour — place this LIVE ${pairLabel(ticket.pair)} order anyway?`)) { setBusy(false); return; }
        if (!window.confirm(`Send a LIVE real-money ${ticket.side.toUpperCase()} of ${units.toLocaleString()} ${pairLabel(ticket.pair)} to OANDA?`)) { setBusy(false); return; }
        const r = await api.post("/oanda/orders", { pair: ticket.pair, side: ticket.side, units, confirm_live: true });
        toast.success(`LIVE ${ticket.side === "buy" ? "Bought" : "Sold"} ${units.toLocaleString()} ${pairLabel(ticket.pair)} @ ${r.data.price}`, { description: "Routed to your OANDA broker" });
      } else {
        const r = await api.post("/forex/orders", { pair: ticket.pair, side: ticket.side, units });
        setAcct(r.data.account);
        toast.success(`${ticket.side === "buy" ? "Bought" : "Sold"} ${units.toLocaleString()} ${pairLabel(ticket.pair)}`);
        loadAcct();
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const close = async (pair) => {
    try { const r = await api.post(`/forex/positions/${pair}/close`); toast.success(`Closed ${pairLabel(pair)}`); setAcct(r.data.account); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const closeAll = async () => {
    if (!window.confirm(`Close ALL ${acct.open_positions} FX position(s) at market?`)) return;
    try {
      const r = await api.post("/forex/positions/close-all");
      const pnl = r.data.realized_pnl ?? 0;
      toast.success(`Closed ${r.data.closed} position(s)`, { description: `Realized P/L: ${pnl >= 0 ? "+" : "-"}$${fmt(Math.abs(pnl))}` });
      setAcct(r.data.account);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setLeverage = async (lev) => {
    try { const r = await api.post("/forex/leverage", { leverage: lev }); setAcct(r.data); toast.success(`Leverage set to ${lev}x`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const resetAcct = async () => {
    if (!window.confirm("Reset your FX paper account to $100,000 and clear all positions?")) return;
    try { const r = await api.post("/forex/account/reset"); setAcct(r.data); toast.success("FX account reset"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Pip risk sizer
  const [risk, setRisk] = useState({ pct: "1", pips: "20" });
  const [sizing, setSizing] = useState(false);
  const sizeIt = async () => {
    if (!acct) return;
    setSizing(true);
    try {
      const r = await api.post("/forex/position-size", {
        pair: ticket.pair, balance: acct.balance,
        risk_pct: Number(risk.pct), stop_pips: Number(risk.pips),
      });
      setTicket((t) => ({ ...t, units: String(r.data.units) }));
      setAmtMode("units");
      toast.success(`Sized ${r.data.units.toLocaleString()} units`, { description: `Risks ~$${fmt(r.data.risk_amount)} (${risk.pct}%) on a ${risk.pips}-pip stop` });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSizing(false); }
  };

  // Copy a paper FX position to the OANDA broker
  const [copyPos, setCopyPos] = useState(null);
  const [copyUnits, setCopyUnits] = useState("");
  const [copyMode, setCopyMode] = useState("units"); // "units" | "usd"
  const [copyUsd, setCopyUsd] = useState("1000");
  const [copyBusy, setCopyBusy] = useState(false);
  const openCopy = (p) => { setCopyMode("units"); setCopyPos({ ...p, source: "copy" }); setCopyUnits(String(Math.abs(p.units))); };
  const copyUnitsResolved = () => {
    if (copyMode === "usd" && copyPos) {
      const upb = usdPerBase(baseOf(copyPos.pair));
      if (!upb || !(Number(copyUsd) > 0)) return null;
      return Math.round(Number(copyUsd) / upb);
    }
    return parseFloat(copyUnits);
  };
  const openSignalOrder = async (p) => {
    const side = p.direction === "bearish" ? "short" : "long";
    let units = 10000, sizeInfo = null;
    try {
      const r = await api.post("/forex/position-size", { pair: p.pair, balance: acct?.balance || 100000, risk_pct: Number(risk.pct), stop_pips: Number(risk.pips) });
      units = r.data.units; sizeInfo = r.data;
    } catch { /* fall back to default units */ }
    setCopyMode("units");
    setCopyPos({ pair: p.pair, side, source: "signal", direction: p.direction, sizeInfo });
    setCopyUnits(String(units));
  };
  const sendToOanda = async () => {
    const units = copyUnitsResolved();
    if (!units || units <= 0) return toast.error(copyMode === "usd" ? "Enter a valid $ amount" : "Enter a valid unit size");
    const isLive = oanda?.active_env === "live";
    const guard = eventWithin(nextEvt, copyPos.pair, 60);
    if (guard && !window.confirm(`⚠ High-impact ${guard.currency} event ("${guard.title}") is due within the hour. Volatility can spike — place this ${pairLabel(copyPos.pair)} order anyway?`)) return;
    if (isLive && !window.confirm(`Send a LIVE real-money ${copyPos.side === "long" ? "BUY" : "SELL"} of ${units} ${pairLabel(copyPos.pair)} to OANDA?`)) return;
    setCopyBusy(true);
    try {
      const r = await api.post("/oanda/orders", {
        pair: copyPos.pair, side: copyPos.side === "long" ? "buy" : "sell",
        units, confirm_live: isLive,
      });
      toast.success(`Sent to OANDA (${r.data.environment})`, { description: `${copyPos.side === "long" ? "Bought" : "Sold"} ${units.toLocaleString()} ${pairLabel(copyPos.pair)} @ ${r.data.price}` });
      setCopyPos(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setCopyBusy(false); }
  };

  // FX price/score alerts (delivered via Telegram)
  const [alertPair, setAlertPair] = useState(null);
  const [aType, setAType] = useState("price_above");
  const [aThreshold, setAThreshold] = useState("");
  const [aBusy, setABusy] = useState(false);
  const openAlert = (p) => {
    setAlertPair(p);
    setAType(p.direction === "bearish" ? "price_below" : "price_above");
    setAThreshold(p.price != null ? String(p.price) : "");
  };
  const createAlert = async () => {
    if (!(Number(aThreshold) > 0)) { toast.error("Enter a valid threshold"); return; }
    setABusy(true);
    try {
      await api.post("/alerts", { ticker: alertPair.pair, alert_type: aType, threshold: Number(aThreshold) });
      toast.success(`Alert set on ${pairLabel(alertPair.pair)}`, { description: "Fires on the Alerts page & pings your linked Telegram." });
      setAlertPair(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setABusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="forex-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2.5">
            <CurrencyDollar size={26} weight="fill" className="text-volt" />
            <h1 className="font-heading text-3xl font-bold tracking-tight">Forex{liveFeed && <span className="mono ml-3 align-middle text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border text-signal-green border-signal-green/40 bg-signal-green/10" data-testid="fx-live-badge"><span className="live-dot mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-signal-green" />Live ticks</span>}</h1>
            {oanda?.linked && oanda?.active_env === "live" ? (
              <span data-testid="fx-mode-badge" className="mono text-[10px] uppercase px-2 py-0.5 rounded-sm bg-signal-red/15 text-signal-red border border-signal-red/30 flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full bg-signal-red animate-pulse" /> FX Spot · Live</span>
            ) : (
              <span data-testid="fx-mode-badge" className="mono text-[10px] uppercase px-2 py-0.5 rounded-sm bg-volt/15 text-volt">FX Spot · Paper</span>
            )}
          </div>
          <p className="mono text-[11px] text-muted-foreground mt-1">
            {oanda?.linked && oanda?.active_env === "live"
              ? "Live orders route to your OANDA broker below · the account panel here stays a paper simulator"
              : "24 pairs · AI relative strength scored vs the US Dollar Index (DXY) · 24/5 market"}
          </p>
        </div>
        <Button data-testid="fx-reset-btn" onClick={resetAcct} variant="outline" className="rounded-sm border-border gap-2 h-9"><ArrowClockwise size={15} /> Reset</Button>
      </div>

      {/* Account summary */}
      {acct && (
        <div className="border border-border bg-card p-5 rounded-sm" data-testid="fx-account">
          <div className="flex flex-wrap gap-5 items-center">
            <Stat label="Balance" value={`$${fmt(acct.balance)}`} />
            <Stat label="Equity" value={`$${fmt(acct.equity)}`} />
            <Stat label="Unrealized P/L" value={`${acct.unrealized_pnl >= 0 ? "+" : "-"}$${fmt(Math.abs(acct.unrealized_pnl))}`} accent={acct.unrealized_pnl >= 0 ? "text-signal-green" : "text-signal-red"} />
            <Stat label="Free Margin" value={`$${fmt(acct.free_margin)}`} />
            <Stat label="Used Margin" value={`$${fmt(acct.used_margin)}`} />
            <div className="min-w-[140px]">
              <div className="label-xs text-muted-foreground">Leverage</div>
              <div className="flex gap-1 mt-1">
                {[1, 10, 20, 50].map((l) => (
                  <button key={l} data-testid={`fx-lev-${l}`} onClick={() => setLeverage(l)}
                    className={`mono text-[11px] px-2 py-1 rounded-sm border transition-colors duration-150 ${acct.leverage === l ? "border-volt bg-volt/15 text-volt" : "border-border text-muted-foreground hover:text-white"}`}>{l}x</button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order ticket */}
      <div className="border border-border bg-card p-5 rounded-sm" data-testid="fx-order-ticket">
        <h2 className="label-xs mb-3">Place FX Order</h2>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <div className="label-xs text-muted-foreground mb-1">Pair</div>
            <select data-testid="fx-pair-select" value={ticket.pair} onChange={(e) => setTicket({ ...ticket, pair: e.target.value })}
              className="bg-background border border-border rounded-sm h-10 px-3 mono text-sm min-w-[130px]">
              {(pairs || []).map((p) => <option key={p.pair} value={p.pair}>{pairLabel(p.pair)}</option>)}
            </select>
          </div>
          <div>
            <div className="label-xs text-muted-foreground mb-1">Side</div>
            <div className="flex rounded-sm overflow-hidden border border-border h-10">
              <button data-testid="fx-side-buy" onClick={() => setTicket({ ...ticket, side: "buy" })} className={`px-4 mono text-sm ${ticket.side === "buy" ? "bg-signal-green/20 text-signal-green" : "text-muted-foreground"}`}>BUY</button>
              <button data-testid="fx-side-sell" onClick={() => setTicket({ ...ticket, side: "sell" })} className={`px-4 mono text-sm ${ticket.side === "sell" ? "bg-signal-red/20 text-signal-red" : "text-muted-foreground"}`}>SELL</button>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1 gap-3">
              <div className="label-xs text-muted-foreground">{amtMode === "usd" ? "$ Amount (USD)" : "Units (base ccy)"}</div>
              <div className="flex rounded-sm overflow-hidden border border-border h-5" data-testid="fx-amt-toggle">
                {["units", "usd"].map((m) => (
                  <button key={m} data-testid={`fx-amt-${m}`} onClick={() => setAmtMode(m)}
                    className={`mono text-[9px] uppercase px-1.5 transition-colors duration-150 ${amtMode === m ? "bg-volt/20 text-volt" : "text-muted-foreground hover:text-white"}`}>{m === "usd" ? "$" : "Units"}</button>
                ))}
              </div>
            </div>
            {amtMode === "usd" ? (
              <div className="relative w-[140px]">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 mono text-sm text-muted-foreground">$</span>
                <Input data-testid="fx-usd-input" type="number" step="any" value={usdAmt} onChange={(e) => setUsdAmt(e.target.value)} className="bg-background rounded-sm mono h-10 w-[140px] pl-6" />
              </div>
            ) : (
              <Input data-testid="fx-units-input" type="number" value={ticket.units} onChange={(e) => setTicket({ ...ticket, units: e.target.value })} className="bg-background rounded-sm mono h-10 w-[140px]" />
            )}
            {amtMode === "usd" && (
              <div className="mono text-[10px] text-muted-foreground mt-1" data-testid="fx-usd-estimate">≈ {ticketUnits() ? ticketUnits().toLocaleString() : "—"} units</div>
            )}
          </div>
          <Button data-testid="fx-submit-order" onClick={submit} disabled={busy} className={`rounded-sm h-10 text-white ${ticket.side === "buy" ? "bg-signal-green hover:bg-signal-green/90 !text-black" : "bg-signal-red hover:bg-signal-red/90"}`}>
            {busy ? "Placing…" : `${ticket.side === "buy" ? "Buy" : "Sell"} ${pairLabel(ticket.pair)}`}
          </Button>
        </div>
        <p className="mono text-[10px] text-muted-foreground mt-2 flex items-center gap-1"><Info size={11} /> Shorting supported. Margin = notional ÷ leverage. P/L shown in USD.</p>
        <div className="mt-2">
          <AmountChips cash={acct ? acct.free_margin * acct.leverage : null}
            onPick={(v) => { setAmtMode("usd"); setUsdAmt(String(v)); }} testid="fx-chips" />
          {acct && <div className="mono text-[10px] text-muted-foreground mt-1">Notional buying power ${(acct.free_margin * acct.leverage).toLocaleString(undefined, { maximumFractionDigits: 0 })} ({acct.leverage}× on ${fmt(acct.free_margin, 0)} free margin)</div>}
        </div>
        {oanda?.linked && oanda?.active_env === "live" && (
          <label className="flex items-center gap-2 cursor-pointer mt-3 border border-signal-red/40 bg-signal-red/5 rounded-sm px-3 py-2 w-fit" data-testid="fx-route-live-label">
            <Checkbox data-testid="fx-route-live" checked={routeLive} onCheckedChange={(v) => setRouteLive(!!v)} className="border-signal-red data-[state=checked]:bg-signal-red" />
            <span className="mono text-[11px] text-signal-red">Route this ticket to my OANDA broker (LIVE real money) instead of the paper simulator</span>
          </label>
        )}
        {/* Pip risk sizer */}
        <div className="mt-3 pt-3 border-t border-border/50 flex flex-wrap gap-3 items-end" data-testid="fx-risk-sizer">
          <div className="flex items-center gap-1.5 text-volt"><Calculator size={14} /> <span className="label-xs">Risk Sizer</span></div>
          <div>
            <div className="label-xs text-muted-foreground mb-1">Risk %</div>
            <Input data-testid="fx-risk-pct" type="number" step="any" value={risk.pct} onChange={(e) => setRisk({ ...risk, pct: e.target.value })} className="bg-background rounded-sm mono h-9 w-[90px]" />
          </div>
          <div>
            <div className="label-xs text-muted-foreground mb-1">Stop (pips)</div>
            <Input data-testid="fx-risk-pips" type="number" step="any" value={risk.pips} onChange={(e) => setRisk({ ...risk, pips: e.target.value })} className="bg-background rounded-sm mono h-9 w-[90px]" />
          </div>
          <Button data-testid="fx-size-btn" onClick={sizeIt} disabled={sizing || !acct} variant="outline" className="rounded-sm h-9 border-volt/40 text-volt hover:bg-volt/10 gap-1.5">{sizing ? "Sizing…" : "Size units"}</Button>
          <span className="mono text-[10px] text-muted-foreground">Sets units so a stop-out risks that % of your ${acct ? fmt(acct.balance, 0) : "—"} balance on {pairLabel(ticket.pair)}.</span>
        </div>
      </div>

      {/* Open positions */}
      {acct && acct.positions?.length > 0 && (
        <div className="border border-border bg-card rounded-sm overflow-hidden" data-testid="fx-positions">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <span className="label-xs">Open Positions ({acct.open_positions})</span>
            <Button data-testid="fx-close-all-btn" onClick={closeAll} variant="outline" size="sm" className="rounded-sm border-signal-red/40 text-signal-red hover:bg-signal-red/10 gap-1.5 h-8"><XCircle size={14} weight="fill" /> Close All</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="label-xs text-muted-foreground border-b border-border/60">
                {["Pair", "Side", "Units", "Avg", "Current", "Pips", "P/L (USD)", ""].map((h) => <th key={h} className="text-left font-normal px-4 py-2">{h}</th>)}
              </tr></thead>
              <tbody>
                {acct.positions.map((p) => (
                  <tr key={p.pair} className="border-b border-border/40" data-testid={`fx-pos-${p.pair}`}>
                    <td className="px-4 py-2.5 mono font-medium">{pairLabel(p.pair)}</td>
                    <td className={`px-4 py-2.5 mono uppercase text-xs ${p.side === "long" ? "text-signal-green" : "text-signal-red"}`}>{p.side}</td>
                    <td className="px-4 py-2.5 mono">{fmt(p.units, 0)}</td>
                    <td className="px-4 py-2.5 mono">{fmt(p.avg_price, 5)}</td>
                    <td className="px-4 py-2.5 mono">{fmt(p.current_price, 5)}</td>
                    <td className={`px-4 py-2.5 mono ${p.pips >= 0 ? "text-signal-green" : "text-signal-red"}`}>{p.pips >= 0 ? "+" : ""}{fmt(p.pips, 1)}</td>
                    <td className={`px-4 py-2.5 mono ${p.unrealized_pnl >= 0 ? "text-signal-green" : "text-signal-red"}`}>{p.unrealized_pnl >= 0 ? "+" : "-"}${fmt(Math.abs(p.unrealized_pnl))}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-3 justify-end">
                        {oanda?.linked && (
                          <button data-testid={`fx-copy-oanda-${p.pair}`} onClick={() => openCopy(p)} title="Send this trade to your OANDA broker" className="text-xs text-volt hover:underline flex items-center gap-1"><Bank size={13} weight="fill" /> OANDA</button>
                        )}
                        <button data-testid={`fx-close-${p.pair}`} onClick={() => close(p.pair)} className="text-xs text-muted-foreground hover:text-signal-red">Close</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pairs scanner */}
      <div className="border border-border bg-card rounded-sm overflow-hidden" data-testid="fx-scanner">
        <div className="border-b border-border px-4 py-3 label-xs">FX Pairs · AI Scanner</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="label-xs text-muted-foreground border-b border-border/60">
              {["Pair", "Name", "Price", "Δ pips", "Δ %", "AI Score", "Signal", "Trade · Alert"].map((h) => <th key={h} className="text-left font-normal px-4 py-2">{h}</th>)}
            </tr></thead>
            <tbody>
              {(pairs || []).map((p) => (
                <tr key={p.pair} className="border-b border-border/40 hover:bg-white/[0.02]" data-testid={`fx-row-${p.pair}`}>
                  <td className="px-4 py-2.5 mono font-medium">
                    <div className="flex items-center gap-1.5">
                      {pairLabel(p.pair)}
                      <EventTag pair={p} nextEvt={nextEvt} today={eventCcy} />
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground text-xs">{p.name}</td>
                  <td className="px-4 py-2.5 mono">{p.live && <span className="live-dot mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-signal-green align-middle" title="Live OANDA tick" />}{fmt(p.price, 5)}</td>
                  <td className={`px-4 py-2.5 mono ${(p.change_pips || 0) >= 0 ? "text-signal-green" : "text-signal-red"}`}>{(p.change_pips || 0) >= 0 ? "+" : ""}{fmt(p.change_pips, 1)}</td>
                  <td className={`px-4 py-2.5 mono ${(p.change_pct || 0) >= 0 ? "text-signal-green" : "text-signal-red"}`}>{(p.change_pct || 0) >= 0 ? "+" : ""}{fmt(p.change_pct)}%</td>
                  <td className="px-4 py-2.5 mono font-semibold">{p.score ?? "—"}</td>
                  <td className="px-4 py-2.5">
                    {p.direction && <span className={`mono text-[10px] uppercase px-2 py-0.5 rounded-sm flex items-center gap-1 w-fit ${p.direction === "bullish" ? "bg-signal-green/15 text-signal-green" : p.direction === "bearish" ? "bg-signal-red/15 text-signal-red" : "bg-white/10 text-muted-foreground"}`}>
                      {p.direction === "bullish" ? <CaretUp size={10} weight="bold" /> : p.direction === "bearish" ? <CaretDown size={10} weight="bold" /> : null}{p.direction}</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-3">
                      <button data-testid={`fx-trade-${p.pair}`} onClick={() => { setTicket({ ...ticket, pair: p.pair }); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="text-xs text-volt hover:underline">Trade</button>
                      {oanda?.linked && p.direction && p.direction !== "neutral" && (
                        <button data-testid={`fx-signal-oanda-${p.pair}`} onClick={() => openSignalOrder(p)} title={`One-tap OANDA ${p.direction === "bullish" ? "BUY" : "SELL"} sized by your Risk Sizer`} className="text-xs text-volt hover:underline flex items-center gap-1"><Bank size={13} weight="fill" /></button>
                      )}
                      <button data-testid={`fx-alert-${p.pair}`} onClick={() => openAlert(p)} title="Set a price / AI-score alert (Telegram)" className="text-muted-foreground hover:text-volt transition-colors"><BellSimple size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {pairs === null && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground mono text-xs">Loading FX pairs…</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Economic calendar */}
      <EconomicPanel />

      {/* Live FX broker (OANDA) */}
      <OandaBroker />

      {/* Copy paper position to OANDA */}
      <Dialog open={!!copyPos} onOpenChange={(o) => !o && setCopyPos(null)}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="fx-copy-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Bank size={18} weight="fill" className="text-volt" /> Send to OANDA · {copyPos && pairLabel(copyPos.pair)}</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              {copyPos?.source === "signal"
                ? `One-tap ${copyPos?.side === "long" ? "BUY" : "SELL"} sized by your Risk Sizer (${risk.pct}% / ${risk.pips}-pip stop) on your OANDA ${oanda?.active_env || ""} account.`
                : `Places a market ${copyPos?.side === "long" ? "BUY" : "SELL"} on your OANDA ${oanda?.active_env || ""} account. This does not change your paper position.`}
            </DialogDescription>
          </DialogHeader>
          {copyPos && (
            <div className="space-y-4">
              <div className={`border rounded-sm px-3 py-2 mono text-[11px] ${oanda?.active_env === "live" ? "border-signal-red/40 bg-signal-red/5 text-signal-red" : "border-volt/30 bg-volt/5 text-volt"}`}>
                {oanda?.active_env === "live" ? "LIVE · REAL MONEY order" : "PRACTICE · demo order"} · {copyPos.side === "long" ? "BUY" : "SELL"} {pairLabel(copyPos.pair)}
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="label-xs">{copyMode === "usd" ? "$ Amount (USD)" : "Units"}</div>
                  <div className="flex rounded-sm overflow-hidden border border-border h-5" data-testid="fx-copy-amt-toggle">
                    {["units", "usd"].map((m) => (
                      <button key={m} data-testid={`fx-copy-amt-${m}`} onClick={() => setCopyMode(m)}
                        className={`mono text-[9px] uppercase px-1.5 ${copyMode === m ? "bg-volt/20 text-volt" : "text-muted-foreground"}`}>{m === "usd" ? "$" : "Units"}</button>
                    ))}
                  </div>
                </div>
                {copyMode === "usd" ? (
                  <>
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 mono text-sm text-muted-foreground">$</span>
                      <Input data-testid="fx-copy-usd" type="number" step="any" value={copyUsd} onChange={(e) => setCopyUsd(e.target.value)} className="bg-background rounded-sm mono pl-6" />
                    </div>
                    <div className="mono text-[10px] text-muted-foreground mt-1" data-testid="fx-copy-usd-estimate">≈ {copyUnitsResolved() ? copyUnitsResolved().toLocaleString() : "—"} units</div>
                    <div className="mt-2"><AmountChips onPick={(v) => setCopyUsd(String(v))} testid="fx-copy-chips" /></div>
                  </>
                ) : (
                  <Input data-testid="fx-copy-units" type="number" value={copyUnits} onChange={(e) => setCopyUnits(e.target.value)} className="bg-background rounded-sm mono" />
                )}
              </div>
              {copyPos.source === "signal" && copyPos.sizeInfo && (
                <div className="hypo-bg border border-white/10 rounded-sm px-3 py-2" data-testid="fx-signal-preview">
                  <div className="label-xs text-muted-foreground mb-1">Auto-size preview</div>
                  <div className="mono text-sm">{Number(copyUnits).toLocaleString()} units · risks ~${fmt(copyPos.sizeInfo.risk_amount)}</div>
                  <div className="mono text-[10px] text-muted-foreground">{copyPos.sizeInfo.risk_pct}% of your ${fmt(acct?.balance || 0, 0)} balance on a {copyPos.sizeInfo.stop_pips}-pip stop</div>
                </div>
              )}
              {(() => { const g = eventWithin(nextEvt, copyPos.pair, 60); return g ? (
                <div className="flex items-start gap-2 border border-signal-red/40 bg-signal-red/5 text-signal-red rounded-sm px-3 py-2 mono text-[11px]" data-testid="fx-risk-guard">
                  <Warning size={13} weight="fill" className="mt-0.5 shrink-0" /> High-impact {g.currency} event ("{g.title}") within the hour — expect volatility.
                </div>
              ) : null; })()}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyPos(null)} className="rounded-sm border-border">Cancel</Button>
            <Button data-testid="fx-copy-confirm" disabled={copyBusy} onClick={sendToOanda} className="bg-volt hover:bg-volt/90 text-white rounded-sm">{copyBusy ? "Sending…" : "Send to OANDA"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FX alert dialog */}
      <Dialog open={!!alertPair} onOpenChange={(o) => !o && setAlertPair(null)}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="fx-alert-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><BellSimple size={18} weight="fill" className="text-volt" /> FX Alert · {alertPair && pairLabel(alertPair.pair)}</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">Checked against live FX prices & AI scores. Triggers show on the Alerts page and ping your linked Telegram.</DialogDescription>
          </DialogHeader>
          {alertPair && (
            <div className="space-y-4">
              <div className="hypo-bg border border-white/10 p-3 rounded-sm flex items-center justify-between">
                <div><div className="mono text-sm">{alertPair.name}</div><div className="mono text-[11px] text-muted-foreground">Now {fmt(alertPair.price, 5)} · AI {alertPair.score ?? "—"}</div></div>
                <span className={`mono text-xs uppercase ${alertPair.direction === "bullish" ? "text-signal-green" : alertPair.direction === "bearish" ? "text-signal-red" : "text-muted-foreground"}`}>{alertPair.direction}</span>
              </div>
              <div>
                <div className="label-xs mb-2">Condition</div>
                <Select value={aType} onValueChange={setAType}>
                  <SelectTrigger data-testid="fx-alert-type" className="bg-background rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{ALERT_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <div className="label-xs mb-2">Threshold {aType.startsWith("score") ? "(0–100)" : "(price)"}</div>
                <Input data-testid="fx-alert-threshold" type="number" step="any" value={aThreshold} onChange={(e) => setAThreshold(e.target.value)} className="bg-background rounded-sm mono" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAlertPair(null)} className="rounded-sm border-border">Cancel</Button>
            <Button data-testid="fx-alert-confirm" disabled={aBusy} onClick={createAlert} className="bg-volt hover:bg-volt/90 text-white rounded-sm">{aBusy ? "Setting…" : "Set Alert"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const IMPACT_STYLE = {
  high: "bg-signal-red/15 text-signal-red border-signal-red/30",
  medium: "bg-signal-yellow/15 text-signal-yellow border-signal-yellow/30",
  low: "bg-white/10 text-muted-foreground border-white/10",
};
const dayKey = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }); } catch { return ""; } };
const timeStr = (iso) => { try { return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

function EconomicPanel() {
  const [data, setData] = useState(null);
  const [impact, setImpact] = useState("high");
  const [ccy, setCcy] = useState("all");

  const load = useCallback((imp) => {
    api.get(`/forex/economic?impact=${imp}`).then((r) => setData(r.data)).catch(() => setData({ events: [], currencies: [] }));
  }, []);
  useEffect(() => { load(impact); }, [impact, load]);

  const events = (data?.events || []).filter((e) => ccy === "all" || e.currency === ccy);
  const currencies = data?.currencies || [];
  // group by day preserving sorted order
  const groups = [];
  events.forEach((e) => {
    const k = dayKey(e.date);
    const g = groups.find((x) => x.day === k);
    if (g) g.items.push(e); else groups.push({ day: k, items: [e] });
  });

  return (
    <div className="border border-border bg-card rounded-sm overflow-hidden" data-testid="fx-economic">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
        <span className="label-xs flex items-center gap-2"><CalendarBlank size={15} className="text-volt" /> Economic Calendar · This Week</span>
        <div className="flex items-center gap-2">
          <div className="flex rounded-sm overflow-hidden border border-border h-8">
            {["high", "medium", "all"].map((v) => (
              <button key={v} data-testid={`fx-econ-impact-${v}`} onClick={() => setImpact(v)}
                className={`px-3 mono text-[11px] uppercase transition-colors duration-150 ${impact === v ? "bg-volt/15 text-volt" : "text-muted-foreground hover:text-white"}`}>{v}</button>
            ))}
          </div>
          <select data-testid="fx-econ-ccy" value={ccy} onChange={(e) => setCcy(e.target.value)}
            className="bg-background border border-border rounded-sm h-8 px-2 mono text-[11px]">
            <option value="all">All CCY</option>
            {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      {data === null ? (
        <div className="px-4 py-8 text-center text-muted-foreground mono text-xs">Loading economic events…</div>
      ) : events.length === 0 ? (
        <div className="px-4 py-8 text-center text-muted-foreground mono text-xs">No {impact !== "all" ? impact + "-impact " : ""}events{ccy !== "all" ? ` for ${ccy}` : ""} this week.</div>
      ) : (
        <div className="overflow-x-auto">
          {groups.map((g) => (
            <div key={g.day}>
              <div className="bg-white/[0.03] px-4 py-1.5 mono text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border/40">{g.day}</div>
              <table className="w-full text-sm">
                <tbody>
                  {g.items.map((e, i) => (
                    <tr key={`${e.title}-${i}`} className="border-b border-border/30 hover:bg-white/[0.02]" data-testid={`fx-econ-row`}>
                      <td className="px-4 py-2.5 mono text-xs text-muted-foreground w-[70px]">{timeStr(e.date)}</td>
                      <td className="px-2 py-2.5 w-[56px]"><span className="mono text-[11px] font-medium">{e.currency}</span></td>
                      <td className="px-2 py-2.5">{e.title}</td>
                      <td className="px-2 py-2.5 mono text-xs text-right text-muted-foreground w-[110px]">{e.forecast != null ? `f/c ${e.forecast}` : ""}</td>
                      <td className="px-2 py-2.5 mono text-xs text-right text-muted-foreground w-[110px]">{e.previous != null ? `prev ${e.previous}` : ""}</td>
                      <td className="px-4 py-2.5 w-[80px] text-right">
                        <span className={`mono text-[9px] uppercase px-1.5 py-0.5 rounded-sm border ${IMPACT_STYLE[e.impact] || IMPACT_STYLE.low}`}>{e.impact}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
      <div className="px-4 py-2 mono text-[10px] text-muted-foreground border-t border-border/40">Source: Forex Factory weekly feed · times shown in your local timezone · schedules & forecasts can change.</div>
    </div>
  );
}
